import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../../../services/api";
import {
  Typography,
  Spin,
  Button,
  message,
  Empty,
  Tag,
  Space,
  Popconfirm,
  Pagination,
  Select,
  Modal,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  AreaChartOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "./MyStalls.css";

const { Text, Paragraph } = Typography;

function MyStalls() {
  const [stalls, setStalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    pendingCount: 0,
    totalRevenue: 0,
  });
  const [isMobileFilter, setIsMobileFilter] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 460 : false
  );
  const navigate = useNavigate();

  const fetchMyStalls = useCallback(
    async (pageNumber = 1) => {
      if (fetching) return;

      try {
        setFetching(true);
        setLoading(true);

        const res = await API.get(
          `/stalls/my/listings?page=${pageNumber}&filter=${filter}`
        );

        setStalls(res.data.stalls || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
        setStats(res.data.stats || {});
      } catch (err) {
        console.error("Fetch error:", err);
        message.error("Failed to load listings");
        setStalls([]);
      } finally {
        setLoading(false);
        setFetching(false);
      }
    },
    [fetching, filter]
  );

  useEffect(() => {
    setPage(1);
    fetchMyStalls(1);
  }, [filter]);

  useEffect(() => {
    fetchMyStalls(page);
  }, [page]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileFilter(window.innerWidth <= 460);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setActionLoadingId(id);
      const endpoint = currentStatus === "active" ? "/deactivate" : "/activate";
      await API.put(`/stalls/${id}${endpoint}`);
      message.success(currentStatus === "active" ? "Deactivated" : "Activated");
      fetchMyStalls(page);
    } catch (err) {
      message.error("Status update failed");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete Listing",
      content: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await API.delete(`/stalls/${id}`);
          message.success("Listing deleted successfully");
          setStalls((prev) => prev.filter((s) => s._id !== id));
        } catch (err) {
          message.error("Failed to delete listing");
        }
      },
    });
  };

  const formatPrice = (price, durationType) => {
    if (!price) return "Negotiable";
    const formatted = `₹${Number(price).toLocaleString("en-IN")}`;
    if (durationType === "daily") return `${formatted}/day`;
    if (durationType === "weekly") return `${formatted}/week`;
    if (durationType === "monthly") return `${formatted}/mo`;
    return formatted;
  };

  const getStatusConfig = (status) =>
    ({
      active: { color: "#10b981", bg: "#ecfdf5", label: "Active" },
      inactive: { color: "#6b7280", bg: "#f3f4f6", label: "Inactive" },
    })[status] || { color: "#ef4444", bg: "#fef2f2", label: "Error" };

  const getApprovalConfig = (status) =>
    ({
      approved: { color: "#3b82f6", bg: "#eff6ff", label: "Approved" },
      pending: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
      rejected: { color: "#ef4444", bg: "#fef2f2", label: "Rejected" },
    })[status] || { color: "#6b7280", bg: "#f3f4f6", label: "Unknown" };

  if (loading) {
    return (
      <div className="stl-page">
        <div className="stl-shell">
          <div className="stl-loading">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  const activeCount = stats.activeCount;
  const pendingCount = stats.pendingCount;
  const totalRevenue = stats.totalRevenue;

  const filteredStalls = stalls;

  const filterOptions = [
    { label: "All", value: "all" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  return (
    <div className="stl-page">
      <div className="stl-shell">
        {/* ── OVERVIEW ── */}
        <section className="stl-overview">
          <div className="stl-overview__stats">
            <div className="stl-overview__stat">
              <div className="stl-overview__value">{stats.total}</div>
              <div className="stl-overview__label">Total Listings</div>
            </div>

            <div className="stl-overview__stat">
              <div className="stl-overview__value stl-overview__value--active">
                {activeCount}
              </div>
              <div className="stl-overview__label">Active</div>
            </div>

            <div className="stl-overview__stat">
              <div className="stl-overview__value stl-overview__value--pending">
                {pendingCount}
              </div>
              <div className="stl-overview__label">Pending Review</div>
            </div>

            <div className="stl-overview__stat">
              <div className="stl-overview__value stl-overview__value--revenue">
                ₹{totalRevenue.toLocaleString()}
              </div>
              <div className="stl-overview__label">Est. Value</div>
            </div>
          </div>

          <Link to="/add-stall" className="stl-overview__cta">
            <Button size="large" type="primary" icon={<PlusOutlined />}>
              Create New Stall
            </Button>
          </Link>
        </section>

        {/* ── TOOLBAR ── */}
        <section className="stl-toolbar">
          <div className="stl-toolbar__left">
            <div className="stl-toolbar__title-wrap">
              <Text className="stl-toolbar__title">My Stalls</Text>
              <Text className="stl-toolbar__subtitle">
                Manage all your stall listings in one place
              </Text>
            </div>
          </div>

          <div className="stl-toolbar__right">
            {isMobileFilter ? (
              <Select
                value={filter}
                onChange={setFilter}
                options={filterOptions}
                className="stl-filters__select"
                classNames={{ popup: { root: "stl-filters__dropdown" } }}
                size="middle"
              />
            ) : (
              <div className="stl-filters">
                <Space wrap size={[10, 10]}>
                  <Button
                    type={filter === "all" ? "primary" : "default"}
                    onClick={() => setFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    type={filter === "approved" ? "primary" : "default"}
                    onClick={() => setFilter("approved")}
                  >
                    Approved
                  </Button>
                  <Button
                    type={filter === "rejected" ? "primary" : "default"}
                    onClick={() => setFilter("rejected")}
                  >
                    Rejected
                  </Button>
                  <Button
                    type={filter === "active" ? "primary" : "default"}
                    onClick={() => setFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    type={filter === "inactive" ? "primary" : "default"}
                    onClick={() => setFilter("inactive")}
                  >
                    Inactive
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </section>

        {/* ── EMPTY ── */}
        {stalls.length === 0 ? (
          <div className="stl-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Text strong className="stl-empty__title">
                    No stalls yet
                  </Text>
                  <Paragraph className="stl-empty__text">
                    Create your first stall listing to get started
                  </Paragraph>
                </div>
              }
            >
              <Link to="/add-stall">
                <Button type="primary" size="large">
                  Create New Stall
                </Button>
              </Link>
            </Empty>
          </div>
        ) : (
          <section className="stl-grid">
            {filteredStalls.map((stall) => (
              <article className="stl-card" key={stall._id}>
                {/* ── Media ── */}
                <div className="stl-card__media">
                  <img
                    src={
                      stall.thumbnail
                        ? stall.thumbnail + "?v=" + stall.updatedAt
                        : "/no-image.png"
                    }
                    alt={stall.title}
                    className="stl-card__image"
                  />

                  <div className="stl-card__badges">
                    <Tag
                      className="stl-card__badge"
                      style={{
                        background: getStatusConfig(stall.status).bg,
                        color: getStatusConfig(stall.status).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getStatusConfig(stall.status).label}
                    </Tag>
                    <Tag
                      className="stl-card__badge"
                      style={{
                        background: getApprovalConfig(stall.approvalStatus).bg,
                        color: getApprovalConfig(stall.approvalStatus).color,
                        borderColor: "transparent",
                      }}
                    >
                      {getApprovalConfig(stall.approvalStatus).label}
                    </Tag>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(stall._id, stall.title)}
                      className="stl-card__delete-btn"
                      danger
                    />
                  </div>
                </div>

                {/* ── Body ── */}
                <div className="stl-card__body">
                  <Text className="stl-card__title" title={stall.title}>
                    {stall.title}
                  </Text>

                  <div className="stl-card__price">
                    <Text className="stl-card__price-value">
                      {formatPrice(
                        stall.pricing?.price,
                        stall.durationDetails?.durationType
                      )}
                    </Text>
                  </div>

                  <div className="stl-card__meta">
                    <div className="stl-card__meta-item">
                      <EnvironmentOutlined className="stl-card__meta-icon" />
                      <span>{stall.location?.city || "N/A"}</span>
                    </div>

                    <div className="stl-card__meta-item">
                      <AreaChartOutlined className="stl-card__meta-icon" />
                      <span>{stall.stallDetails?.areaSqFt || 0} sqft</span>
                    </div>
                  </div>

                  {/* ── Actions ── */}
                  <div className="stl-card__footer">
                    <div className="stl-card__actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/edit-stall/${stall._id}`)}
                        className="stl-btn stl-btn--secondary"
                      >
                        Edit
                      </Button>

                      <Popconfirm
                        title={`Mark as ${stall.status === "active" ? "Inactive" : "Active"}`}
                        description="This will update visibility immediately."
                        onConfirm={() =>
                          handleStatusToggle(stall._id, stall.status)
                        }
                        okText="Confirm"
                        cancelText="Cancel"
                        okButtonProps={{ danger: stall.status === "active" }}
                      >
                        <Button
                          size="small"
                          loading={actionLoadingId === stall._id}
                          className={`stl-btn ${stall.status === "active"
                            ? "stl-btn--active"
                            : "stl-btn--inactive"
                            }`}
                        >
                          {stall.status === "active" ? "Deactivate" : "Activate"}
                        </Button>
                      </Popconfirm>

                      <Link
                        to={`/stall/${stall.slug}`}
                        className="stl-card__view-link"
                      >
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          className="stl-btn stl-btn--primary"
                        >
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}

        {/* ── PAGINATION ── */}
        {total > 0 && (
          <div className="stl-pagination">
            <Pagination
              current={page}
              total={total}
              pageSize={15}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
              responsive
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MyStalls;